import { Button } from "@/components/generic/Button";
import { Heading } from "@/components/generic/Heading";
import { Separator } from "@/components/generic/Separator";
import { TextField } from "@/components/generic/TextField";
import { CardWithSection } from "@/components/layout/CardWithSection";
import { PageContent } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import { type LoginResponse } from "@/services/auth.service";
import { Organization, User } from "@/types/auth";
import { api, queryClient } from "@/utils/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const formSchema = z.object({
  company: z.object({
    name: z.string().min(1, "Dette felt er påkrævet"),
    address1: z.string(),
    address2: z.string(),
    zipCode: z.string(),
    city: z.string(),
  }),
  contactPerson: z.object({
    name: z.string().min(1, "Dette felt er påkrævet"),
    email: z.string().min(1, "Dette felt er påkrævet").email("Ugyldig e-mail"),
    phone: z.union([
      z
        .string()
        .trim()
        .regex(/[0-9]/g, "Telefonnummer skal indeholde tal")
        .regex(/^[+()0-9\- ]+$/, "Ugyldigt telefonnummer"),
      z.literal(""),
    ]),
  }),
  invoiceEmail: z.union([z.string().email("Ugyldig e-mail"), z.literal("")]),
});

export const Route = createFileRoute("/_dashboard/settings/_admin/company/")({
  component: RouteComponent,
  staticData: {
    meta: {
      title: "Firmaoplysninger",
    },
  },
});

function RouteComponent() {
  const context = Route.useRouteContext();

  const { mutateAsync: updateOrganization, isPending: isUpdatingOrganization } =
    useMutation({
      mutationFn: async (data: z.infer<typeof formSchema>) => {
        const user = await api.get<User>("users/me").json();

        return api
          .put<Organization>("organizations/me", {
            json: {
              ...user.organization,
              name: data.company.name,
              address1: data.company.address1,
              address2: data.company.address2,
              zipCode: data.company.zipCode,
              city: data.company.city,
              billingResponsibleEmail: data.invoiceEmail,
              contactName: data.contactPerson.name,
              contactEmail: data.contactPerson.email,
              contactPhone: data.contactPerson.phone,
            } satisfies Organization,
          })
          .json();
      },
      onSuccess: (organization) => {
        queryClient.setQueryData(
          ["auth"],
          (auth: LoginResponse | null | undefined) => {
            if (!auth) return auth;
            return {
              ...auth,
              user: {
                ...auth.user,
                organization,
              },
            } satisfies LoginResponse;
          },
        );
      },
      onError: (error) => {
        console.error("Could not update organization", error);
        // TODO : Show toast or something
      },
    });

  const form = useForm({
    disabled: isUpdatingOrganization,
    resolver: zodResolver(formSchema),
    defaultValues: {
      company: {
        name: context.user?.organization.name ?? "",
        address1: context.user?.organization.address1 ?? "",
        address2: context.user?.organization.address2 ?? "",
        zipCode: context.user?.organization.zipCode ?? "",
        city: context.user?.organization.city ?? "",
      },
      invoiceEmail: context.user?.organization.billingResponsibleEmail ?? "",
      contactPerson: {
        name: context.user?.organization.contactName ?? "",
        email: context.user?.organization.contactEmail ?? "",
        phone: context.user?.organization.contactPhone ?? "",
      },
    },
  });

  const submit = form.handleSubmit(async (data) => {
    await updateOrganization(data);
    toast.success("Firmaoplysninger opdateret");
  });

  return (
    <div className="mb-14 flex h-full flex-col">
      <PageHeader title="Firmaoplysninger" />

      <PageContent className="flex h-full flex-col pt-7">
        <form
          className="mx-auto flex w-full max-w-[448px] flex-col pt-6 pb-2"
          onSubmit={submit}
        >
          <div className="flex flex-col gap-y-16">
            <div className="flex flex-col gap-y-8">
              <Heading className="text-center">Firma</Heading>

              <CardWithSection>
                <TextField
                  theme="stacked"
                  control={form.control}
                  name="company.name"
                  aria-label="Firmanavn"
                  placeholder="Firmanavn"
                />

                <TextField
                  theme="stacked"
                  control={form.control}
                  name="company.address1"
                  aria-label="Adresse 1"
                  placeholder="Adresse 1"
                />

                <TextField
                  theme="stacked"
                  control={form.control}
                  name="company.address2"
                  aria-label="Adresse 2"
                  placeholder="Adresse 2"
                />

                <div className="flex">
                  <div className="w-0 max-w-3/10 shrink-0 grow">
                    <TextField
                      theme="stacked"
                      control={form.control}
                      name="company.zipCode"
                      aria-label="Postnr."
                      placeholder="Postnr."
                    />
                  </div>

                  <Separator orientation="vertical" className="mx-5" />

                  <div className="w-0 max-w-7/10 shrink-0 grow">
                    <TextField
                      theme="stacked"
                      control={form.control}
                      name="company.city"
                      aria-label="By"
                      placeholder="By"
                    />
                  </div>
                </div>
              </CardWithSection>
            </div>

            <div className="flex flex-col gap-y-8">
              <Heading className="text-center">Kontaktperson</Heading>

              <CardWithSection>
                <TextField
                  theme="stacked"
                  control={form.control}
                  name="contactPerson.name"
                  aria-label="Navn"
                  placeholder="Navn"
                />

                <TextField
                  theme="stacked"
                  control={form.control}
                  name="contactPerson.email"
                  type="email"
                  aria-label="E-mail"
                  placeholder="E-mail"
                />

                <TextField
                  theme="stacked"
                  control={form.control}
                  name="contactPerson.phone"
                  type="tel"
                  aria-label="Telefonnummer"
                  placeholder="Telefonnummer"
                />
              </CardWithSection>
            </div>

            <div className="flex flex-col gap-y-8">
              <div className="flex flex-col gap-y-3">
                <Heading className="text-center">Send fakturaer til</Heading>

                <p className="text-theme-on-background-variant text-center text-xs font-medium">
                  Send fakturaer til en anden e-mail end kontaktpersonens
                </p>
              </div>

              <CardWithSection>
                <TextField
                  theme="stacked"
                  control={form.control}
                  name="invoiceEmail"
                  type="email"
                  aria-label="E-mail"
                  placeholder="E-mail"
                />
              </CardWithSection>
            </div>
          </div>

          <Button
            className="mx-auto mt-14"
            size="lg"
            type="submit"
            isDisabled={!form.formState.isDirty}
          >
            Gem ændringer
          </Button>
        </form>
      </PageContent>
    </div>
  );
}
